import { useEffect, useState } from "react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  ChangelogEntry,
  ChangelogChange,
  listChangelog,
  createChangelogEntry,
  updateChangelogEntry,
  deleteChangelogEntry,
} from "services/admin/adminChangelog.service";

const TYPE_LABEL: Record<string, string> = { feature: "Novidade", fix: "Correção", improvement: "Melhoria" };

const emptyForm = { version: "", releaseDate: "", changesText: "" };

const parseChanges = (text: string): ChangelogChange[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^\[(feature|fix|improvement)\]\s*(.*)$/i);
      if (match) return { type: match[1].toLowerCase() as ChangelogChange["type"], text: match[2] };
      return { type: "improvement" as const, text: line };
    });

const AdminChangelog = () => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listChangelog()
      .then(setEntries)
      .catch((err) => console.error("[AdminChangelog] erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.version.trim() || !form.releaseDate) return;
    setSaving(true);
    try {
      await createChangelogEntry({
        version: form.version,
        releaseDate: form.releaseDate,
        changes: parseChanges(form.changesText),
        published: false,
      });
      setForm(emptyForm);
      load();
    } catch (err) {
      console.error("[AdminChangelog] erro ao criar:", err);
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (entry: ChangelogEntry) => {
    await updateChangelogEntry(entry.id, { published: !entry.published });
    load();
  };

  const handleDelete = async (id: number) => {
    await deleteChangelogEntry(id);
    load();
  };

  return (
    <AdminLayout title="Changelog" subtitle="Histórico de atualizações da plataforma">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex flex-col gap-4">
          <h3 className="text-[13px] font-semibold text-slate-700">Nova versão</h3>
          <div className="flex flex-col gap-1.5">
            <Label>Versão</Label>
            <Input placeholder="v1.4.0" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Data de lançamento</Label>
            <Input type="date" value={form.releaseDate} onChange={(e) => setForm({ ...form, releaseDate: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Alterações (uma por linha, ex: [fix] corrigido bug X)</Label>
            <textarea
              className="min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.changesText}
              onChange={(e) => setForm({ ...form, changesText: e.target.value })}
              placeholder={"[feature] Nova página de status\n[fix] Corrigido bug no tracking"}
            />
          </div>
          <Button type="submit" disabled={saving} className="bg-brand-sidebar hover:bg-brand-sidebar/90">
            {saving ? "Salvando..." : "Salvar versão"}
          </Button>
        </form>

        <div className="xl:col-span-2 flex flex-col gap-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : entries.length ? (
            entries.map((entry) => (
              <AdminCard key={entry.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm text-slate-800">{entry.version}</h4>
                    <span className="text-xs text-slate-400">{new Date(entry.releaseDate).toLocaleDateString("pt-BR")}</span>
                    <StatusBadge label={entry.published ? "Publicado" : "Rascunho"} tone={entry.published ? "green" : "slate"} />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => togglePublished(entry)}>
                      {entry.published ? "Despublicar" : "Publicar"}
                    </Button>
                    <Button variant="outline" onClick={() => handleDelete(entry.id)}>Excluir</Button>
                  </div>
                </div>
                <ul className="text-sm text-slate-600 flex flex-col gap-1">
                  {entry.changes.map((c, idx) => (
                    <li key={idx}>
                      <StatusBadge label={TYPE_LABEL[c.type]} tone={c.type === "fix" ? "red" : c.type === "feature" ? "blue" : "green"} className="mr-2" />
                      {c.text}
                    </li>
                  ))}
                </ul>
              </AdminCard>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma versão publicada ainda.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChangelog;
